/**
 * GST → 2B recon. The supplier invoice numbers held beside vouchers, as a
 * register a person can read, correct and take back.
 * @file src/web/features/admin/components/InvoiceNumbersPanel.tsx
 *
 * Deliberately NOT a master. A master is a standing definition — a document
 * series, a party, an item — that outlives every voucher. This is one fact
 * about one bill: that voucher, that number. It belongs where it is made and
 * used, beside the reconciliation that needs it. The RULE that decides when a
 * number is held without asking is the master, and lives in Masters.
 *
 * Tally does not send the supplier's own number yet, and GSTR-2B matches on
 * nothing else. So the number is recorded beside the voucher, never on it —
 * writing it onto the voucher marks the voucher edited, and the importer skips
 * edited vouchers whole. Everything on this screen is that one idea: the books
 * stay exactly as the client keeps them, and the number we need for matching
 * lives here.
 *
 * Numbers arrive from GST → Corrections and from the 2B table. This is where
 * they can be seen together, and the only place one can be taken back.
 */

import { useCallback, useEffect, useState } from 'react';
import { Hash, Search, Trash2, Pencil, Plus, Check, X, Loader2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetchJson } from '../lib/admin-api';
import { Card, Th, Td, Pill, EmptyState, Loading, ErrBox, AddToggle, inputCls } from './masters/shared';

interface AliasRow {
  voucherId: string; companyName: string; voucherNumber: string | null; date: string; type: string;
  partyName: string | null; partyGstin: string | null; amount: number;
  alias: string; source: string; note: string | null; createdAt: string;
  supersededBy: string | null; frozen: boolean;
}
interface Candidate {
  voucherId: string; companyName: string; voucherNumber: string | null; date: string; type: string;
  partyName: string | null; partyGstin: string | null; amount: number;
}

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function InvoiceNumbersPanel({ entityId }: { entityId: string | null }) {
  const [rows, setRows] = useState<AliasRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    adminFetchJson<{ rows: AliasRow[] }>('/api/gst/aliases', { query: { entityId, q: q || undefined } })
      .then((d) => { setRows(d.rows ?? []); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entityId, q]);

  // Typing in the search box should not fire a request per keystroke.
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  if (loading && !rows.length) return <Loading label="Loading invoice numbers" />;
  if (error) return <ErrBox msg={error} />;

  const dormant = rows.filter((r) => r.supersededBy).length;

  return (
    <Card
      collapsible
      defaultOpen={false}
      title="Numbers held beside vouchers"
      subtitle="The number the supplier printed on the bill, which GSTR-2B matches on. Tally does not send it yet, so it is held beside the voucher — the voucher itself is never touched, so it keeps updating from the client's Tally."
      action={<AddToggle open={adding} onToggle={() => setAdding(!adding)} label="Hold a number" />}
      summary={
        <span className="text-xs text-ink-500">
          {rows.length} held{dormant ? ` · ${dormant} now dormant` : ''}
        </span>
      }
    >
      {adding && <AddPanel entityId={entityId} onDone={() => { setAdding(false); load(); }} />}

      <div className="flex items-center gap-2 border-b border-ink-100 px-6 py-3">
        <Search className="h-3.5 w-3.5 text-ink-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a number, a party or our voucher no."
          className="w-full max-w-sm bg-transparent text-sm outline-none placeholder:text-ink-400"
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-400" />}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          what="supplier invoice number"
          why={q
            ? 'Nothing matches that search.'
            : 'Numbers are recorded from GST → Corrections, or from a row of the 2B table where the two sides already agree on supplier, date and amount. They appear here once held.'}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <Th>Bill</Th><Th>Party</Th><Th right>Amount</Th>
                <Th>Number held</Th><Th>From</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {rows.map((r) => <Row key={r.voucherId} r={r} entityId={entityId} onChanged={load} />)}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Row({ r, entityId, onChanged }: { r: AliasRow; entityId: string | null; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(r.alias);
  const [confirmForget, setConfirmForget] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await adminFetchJson<any>(`/api/gst/aliases/${r.voucherId}`, {
        method: 'PUT', query: { entityId: entityId ?? undefined }, body: JSON.stringify({ alias: value }),
      });
      if (res?.ok === false) throw new Error(res.error ?? 'Could not save');
      toast.success(`Now held as ${value}.`);
      setEditing(false); onChanged();
    } catch (e) { toast.error((e as Error).message.replace(/^\d+\s/, '')); }
    finally { setBusy(false); }
  };

  const forget = async () => {
    setBusy(true);
    try {
      await adminFetchJson(`/api/gst/aliases/${r.voucherId}`, { method: 'DELETE', query: { entityId: entityId ?? undefined } });
      toast.success('Forgotten. The bill goes back to pairing on amount and date.');
      onChanged();
    } catch (e) { toast.error((e as Error).message.replace(/^\d+\s/, '')); }
    finally { setBusy(false); setConfirmForget(false); }
  };

  return (
    <tr className="hover:bg-ink-50/50">
      <Td>
        <span className="font-medium text-ink-800">{r.date}</span>
        <span className="block text-[11px] text-ink-400">
          ours {r.voucherNumber ?? '—'} · {r.type}
          {r.frozen && <span className="ml-1 text-warn-700" title="This voucher is marked edited, so imports skip it. Unrelated to the number held here.">· frozen</span>}
        </span>
      </Td>
      <Td>
        {r.partyName ?? '—'}
        {r.partyGstin && <span className="block font-mono text-[11px] text-ink-400">{r.partyGstin}</span>}
      </Td>
      <Td right mono>{money(r.amount)}</Td>
      <Td mono>
        {editing ? (
          <span className="inline-flex items-center gap-1.5">
            <input
              autoFocus value={value} onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setValue(r.alias); } }}
              className={`${inputCls} w-36 py-1 text-xs`}
            />
            <button onClick={save} disabled={busy} title="Save" className="rounded-md bg-inkblack px-2 py-1 text-paper disabled:opacity-40">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </button>
            <button onClick={() => { setEditing(false); setValue(r.alias); }} title="Cancel" className="rounded-md border border-ink-300 px-2 py-1 text-ink-500">
              <X className="h-3 w-3" />
            </button>
          </span>
        ) : (
          <>
            <span className={r.supersededBy ? 'text-ink-400 line-through' : 'font-semibold text-ink-900'}>{r.alias}</span>
            {/* Tally's own field always wins. Saying so beats a number that
                silently stopped being used. */}
            {r.supersededBy && (
              <span className="block text-[11px] text-ink-500">
                dormant — Tally now sends <span className="font-semibold">{r.supersededBy}</span>
              </span>
            )}
          </>
        )}
      </Td>
      <Td><Pill tone={r.source === '2b' ? 'brand' : 'neutral'}>{r.source === '2b' ? 'GSTR-2B' : 'typed here'}</Pill></Td>
      <Td right>
        {confirmForget ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-600">
            Forget it? Nothing in the books changes.
            <button onClick={forget} disabled={busy} className="rounded-md bg-danger-600 px-2 py-1 font-semibold text-white disabled:opacity-40">Forget</button>
            <button onClick={() => setConfirmForget(false)} className="rounded-md border border-ink-300 px-2 py-1 font-semibold text-ink-600">Cancel</button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <button onClick={() => setEditing(true)} title="Change the number held" className="rounded-md p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setConfirmForget(true)} title="Stop holding this number" className="rounded-md p-1.5 text-ink-400 hover:bg-danger-50 hover:text-danger-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </Td>
    </tr>
  );
}

/**
 * Adding one by hand. The ordinary route is GST → Corrections, where 2B
 * supplies the number; this is for a bill 2B has not listed — an unregistered
 * supplier's, or one filed late — where only the paper invoice has it.
 */
function AddPanel({ entityId, onDone }: { entityId: string | null; onDone: () => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Candidate[]>([]);
  const [picked, setPicked] = useState<Candidate | null>(null);
  const [num, setNum] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!entityId || picked) return;
    setSearching(true);
    const t = setTimeout(() => {
      adminFetchJson<{ rows: Candidate[] }>('/api/gst/aliases/candidates', { query: { entityId, q: q || undefined } })
        .then((d) => setRows(d.rows ?? []))
        .catch(() => setRows([]))
        .finally(() => setSearching(false));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [entityId, q, picked]);

  const save = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const res = await adminFetchJson<any>(`/api/gst/aliases/${picked.voucherId}`, {
        method: 'PUT', query: { entityId: entityId ?? undefined }, body: JSON.stringify({ alias: num, source: 'manual' }),
      });
      if (res?.ok === false) throw new Error(res.error ?? 'Could not save');
      toast.success(`${num} is now held against voucher ${picked.voucherNumber ?? ''}.`.replace('  ', ' '));
      onDone();
    } catch (e) { toast.error((e as Error).message.replace(/^\d+\s/, '')); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
      {!picked ? (
        <>
          <p className="text-xs font-medium text-ink-600">Which bill? Only purchases with no number yet are listed.</p>
          <div className="mt-2 flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-ink-400" />
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Party name or our voucher no."
              className={`${inputCls} max-w-sm py-1.5 text-sm`}
            />
            {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-400" />}
          </div>
          <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-ink-200 bg-white">
            {rows.length === 0 ? (
              <p className="px-4 py-3 text-xs text-ink-500">
                {q ? 'No bill matches that.' : 'Every purchase already carries a number.'}
              </p>
            ) : rows.map((c) => (
              <button
                key={c.voucherId}
                onClick={() => setPicked(c)}
                className="flex w-full items-center justify-between gap-4 border-b border-ink-50 px-4 py-2.5 text-left last:border-0 hover:bg-ink-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink-800">{c.partyName ?? '—'}</span>
                  <span className="block text-[11px] text-ink-400">{c.date} · ours {c.voucherNumber ?? '—'} · {c.type}</span>
                </span>
                <span className="shrink-0 font-mono text-xs text-ink-600">{money(c.amount)}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-xs font-medium text-ink-600">
            {picked.partyName} · {picked.date} · ours {picked.voucherNumber ?? '—'} · {money(picked.amount)}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              autoFocus value={num} onChange={(e) => setNum(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && num.trim()) save(); }}
              placeholder="Supplier's invoice no."
              className={`${inputCls} w-52 py-1.5 text-sm`}
            />
            <button
              onClick={save} disabled={!num.trim() || busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-inkblack px-3 py-1.5 text-xs font-semibold text-paper disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Hold it
            </button>
            <button onClick={() => { setPicked(null); setNum(''); }} className="text-xs font-medium text-ink-500 hover:text-ink-700">
              Pick another bill
            </button>
          </div>
          <p className="mt-2 text-[11px] text-ink-400">
            <Hash className="mr-1 inline h-3 w-3" />
            Up to 16 characters — letters, digits, / and - only, the same rule GSTN applies.
          </p>
        </>
      )}
    </div>
  );
}
